/**
 * Place preview component
 * Compact place preview card
 */

import { Card, Typography, Rate, Tag, Space, Button, Image, Tooltip } from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { PlaceListItem, Place } from '@/types/place';
import { formatNumber } from '@/utils/formatters';

const { Text, Paragraph, Link } = Typography;

interface PlacePreviewProps {
  place: PlaceListItem | Place;
  onViewDetails?: () => void;
  showActions?: boolean;
  compact?: boolean;
}

export function PlacePreview({
  place,
  onViewDetails,
  showActions = true,
  compact = false,
}: PlacePreviewProps) {
  const handleCopyAddress = () => {
    if (place.address) {
      navigator.clipboard.writeText(place.address);
    }
  };

  if (compact) {
    return (
      <div
        style={{
          padding: 12,
          background: 'var(--color-bg-spotlight)',
          borderRadius: 8,
        }}
      >
        <Space>
          {place.thumbnail && (
            <Image
              src={place.thumbnail}
              width={48}
              height={48}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              preview={false}
            />
          )}
          <div>
            <Text strong>{place.name}</Text>
            {place.category && (
              <Tag size="small" style={{ marginLeft: 8 }}>
                {place.category}
              </Tag>
            )}
            {place.rating && (
              <div style={{ marginTop: 4 }}>
                <Rate
                  disabled
                  value={place.rating}
                  allowHalf
                  style={{ fontSize: 10 }}
                />
                <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                  ({formatNumber(place.reviewCount || 0)})
                </Text>
              </div>
            )}
          </div>
        </Space>
      </div>
    );
  }

  return (
    <Card
      size="small"
      styles={{ body: { padding: 16 } }}
      cover={
        place.thumbnail && (
          <Image
            src={place.thumbnail}
            height={150}
            style={{ objectFit: 'cover' }}
            preview={false}
          />
        )
      }
    >
      {/* Header */}
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>
            {place.name}
          </Text>
          {place.category && (
            <Tag style={{ marginLeft: 8 }}>{place.category}</Tag>
          )}
        </div>

        {/* Rating */}
        {place.rating && (
          <Space size={8}>
            <Rate
              disabled
              value={place.rating}
              allowHalf
              style={{ fontSize: 14 }}
            />
            <Text type="secondary">
              {place.rating.toFixed(1)} ({formatNumber(place.reviewCount || 0)} reviews)
            </Text>
          </Space>
        )}

        {/* Address */}
        {place.address && (
          <Tooltip title="Click to copy">
            <div
              style={{ cursor: 'pointer' }}
              onClick={handleCopyAddress}
            >
              <Space>
                <EnvironmentOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                <Text style={{ fontSize: 13 }}>{place.address}</Text>
              </Space>
            </div>
          </Tooltip>
        )}

        {/* Phone */}
        {place.phone && (
          <Space>
            <PhoneOutlined style={{ color: 'var(--color-text-tertiary)' }} />
            <Text copyable style={{ fontSize: 13 }}>
              {place.phone}
            </Text>
          </Space>
        )}

        {/* Website */}
        {place.website && (
          <Space>
            <GlobalOutlined style={{ color: 'var(--color-text-tertiary)' }} />
            <Link href={place.website} target="_blank" style={{ fontSize: 13 }}>
              {new URL(place.website).hostname}
            </Link>
          </Space>
        )}

        {/* Price level */}
        {place.priceLevel && (
          <Text type="secondary">
            {'$'.repeat(place.priceLevel)} · Price level
          </Text>
        )}

        {/* Actions */}
        {showActions && (
          <Space style={{ marginTop: 8 }}>
            {onViewDetails && (
              <Button size="small" onClick={onViewDetails}>
                View Details
              </Button>
            )}
            {place.url && (
              <Button
                size="small"
                icon={<ExportOutlined />}
                href={place.url}
                target="_blank"
              >
                Maps
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}

/**
 * Horizontal place preview
 */
export function PlacePreviewHorizontal({
  place,
  onClick,
}: {
  place: PlaceListItem;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        borderRadius: 8,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.2s',
      }}
      onMouseOver={(e) => {
        if (onClick) {
          e.currentTarget.style.background = 'var(--color-bg-spotlight)';
        }
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {place.thumbnail && (
        <Image
          src={place.thumbnail}
          width={80}
          height={80}
          style={{ objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
          preview={false}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ display: 'block' }} ellipsis>
          {place.name}
        </Text>
        {place.category && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {place.category}
          </Text>
        )}
        {place.rating && (
          <div style={{ marginTop: 4 }}>
            <Rate
              disabled
              value={place.rating}
              allowHalf
              style={{ fontSize: 10 }}
            />
            <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
              ({formatNumber(place.reviewCount || 0)})
            </Text>
          </div>
        )}
        {place.address && (
          <Paragraph
            type="secondary"
            style={{ fontSize: 12, marginBottom: 0, marginTop: 4 }}
            ellipsis
          >
            {place.address}
          </Paragraph>
        )}
      </div>
    </div>
  );
}

export default PlacePreview;
